import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import BookmarkCard from "./BookmarkCard";
import dashboardButtonSx from "./dashboardButtonSx";
import {
  NAV_INDEX,
  NAV_TREE,
  nodeChainVisible,
} from "../../shared/navigationRegistry";

// The user-curated bookmarks grid at the top of the Dashboard Menu. Replaces
// the old fixed shortcut grid. Each tile resolves a stored nav node id back to
// its registry node (so labels/links stay correct as the registry evolves and
// across schools); unknown ids are dropped from display. The cog above the grid
// (or pressing and holding a tile) enters "manage" mode: tiles become
// drag-sortable (fixed slots via dnd-kit) and gain a delete X, and the cog
// becomes an "Ok" button. Edits (reorder, delete) persist immediately, so "Ok"
// just leaves manage mode.
// eslint-disable-next-line prefer-arrow-callback -- named for a clean displayName
const BookmarksGrid = forwardRef(function BookmarksGrid(
  {
    bookmarkIds,
    isMobile,
    manageMode,
    saving = false,
    highlightId,
    onHighlightEnd,
    onReorder,
    onRemove,
    onActivate,
    onLongPress,
    onManageStart,
    onManageDone,
    // When false (the Home screen reuse), render just the bare tile grid: no
    // section header, no cog/manage controls, and nothing at all when empty.
    // Defaults to the full Dashboard Menu experience.
    showControls = true,
    // Internal padding of each tile. Home tiles get more room to breathe.
    cardPadding = 1,
    // Column count override. Defaults to the launchpad's four-across grid (the
    // dialog is sized so four tiles fit it exactly); Home passes its own.
    columns: columnsProp = null,
    // Center a sparse row instead of left-filling it (Home).
    center = false,
    // Tile surface treatment - see BookmarkCard. The launchpad passes
    // "frosted", as does Home.
    variant = "glass",
    // Current nav context (school/permissions/year). When supplied, a bookmark
    // whose node is not visible for the selected school (permission or feature
    // gate fails) is rendered DISABLED - greyed and non-navigable - rather than
    // removed, so the user can still reorder, delete, or keep it for a school
    // where they do have access. The stored id list is never mutated by this.
    ctx = null,
  },
  ref
) {
  const columns = columnsProp ?? (isMobile ? 3 : 4);

  // Resolve ids -> registry nodes, dropping any unknown id. Nodes the current
  // context gates away stay in the list but are flagged `disabled` (see above).
  // The list of ids drives both the rendered tiles and the sortable item set so
  // reorder indices always line up with what the user sees.
  const entries = useMemo(
    () =>
      bookmarkIds
        .map((id) => {
          const match = NAV_INDEX.find((e) => e.node.id === id);
          if (!match) return null;
          // For sub-links the breadcrumb is [parent, ..., leaf]; surface the
          // immediate parent so the tile shows which section it belongs to.
          const { node, breadcrumb } = match;
          const parentLabel =
            breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2] : null;
          // Icons live on the top-level nav node. Use the link's own icon when
          // it has one, else fall back to its section (top-level ancestor) icon;
          // BookmarkCard falls back to a generic bookmark glyph if neither.
          const ancestor = NAV_TREE.find(
            (n) =>
              n.id === node.id ||
              (n.children ?? []).some((c) => c.id === node.id)
          );
          const Icon = node.icon ?? ancestor?.icon ?? null;
          const disabled = Boolean(ctx) && !nodeChainVisible(match, ctx);
          return { id, node, parentLabel, Icon, disabled };
        })
        .filter(Boolean),
    [bookmarkIds, ctx]
  );
  const validIds = useMemo(() => entries.map((e) => e.id), [entries]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = validIds.indexOf(active.id);
    const newIndex = validIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(validIds, oldIndex, newIndex));
  };

  // ---- Roving tabindex for normal (non-manage) mode --------------------
  const itemRefs = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    setFocusedIndex(0);
  }, [manageMode, entries.length]);

  const setItemRef = (i, el) => {
    itemRefs.current[i] = el;
  };
  const focusItem = (i) => {
    setFocusedIndex(i);
    itemRefs.current[i]?.focus();
  };

  // Let the parent (the Dashboard Menu dialog) put the keyboard on the first
  // tile once the open transition settles, so arrow keys roam the grid right
  // away instead of after tabbing past the close/cog buttons.
  useImperativeHandle(ref, () => ({
    focusFirst: () => focusItem(0),
  }));

  // Clear the transient highlight after a moment (the dialog scroll-to-top that
  // brings the new tile into view is handled by the parent).
  useEffect(() => {
    if (!highlightId) return undefined;
    const timer = setTimeout(() => onHighlightEnd?.(), 1600);
    return () => clearTimeout(timer);
  }, [highlightId, onHighlightEnd]);

  const onItemKeyDown = (e) => {
    if (manageMode) return; // dnd-kit's keyboard sensor owns arrows in manage mode
    const count = entries.length;
    const col = focusedIndex % columns;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      // Disabled bookmarks (gated away at this school) do not navigate.
      if (!entries[focusedIndex]?.disabled)
        onActivate(entries[focusedIndex]?.node);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (col < columns - 1 && focusedIndex + 1 < count)
        focusItem(focusedIndex + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (col > 0) focusItem(focusedIndex - 1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (focusedIndex + columns < count) focusItem(focusedIndex + columns);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (focusedIndex - columns >= 0) focusItem(focusedIndex - columns);
    }
  };

  // A tile is 116px square wherever the grid is used, so the Home screen and the
  // launchpad render the same thing. Only as many tracks as there are tiles are
  // laid out, and capping the grid at their combined width keeps each track at
  // exactly TILE_PX on a roomy container while still letting them shrink (1fr)
  // on a narrow phone rather than overflowing it.
  const TILE_PX = 116;
  const GAP_PX = 12; // matches gap: 1.5
  const trackCount = Math.min(columns, Math.max(entries.length, 1));
  const gridSx = {
    display: "grid",
    gridTemplateColumns: `repeat(${trackCount}, minmax(0, 1fr))`,
    gap: 1.5,
    // Spelled out rather than left to the flex parent's stretch: an auto side
    // margin (the centering below) cancels that stretch, and the grid falls
    // back to the width of its own content - which shrinks the tiles to
    // whatever the icon and label need instead of the tile size.
    width: "100%",
    maxWidth: trackCount * TILE_PX + (trackCount - 1) * GAP_PX,
    // Home centers its block under the greeting; the launchpad fills from the
    // left edge of its content column.
    ...(center && { mx: "auto" }),
  };

  const grid = (
    <Box
      role="grid"
      aria-label="Bookmarks"
      onKeyDown={onItemKeyDown}
      sx={gridSx}
    >
      {entries.map((entry, i) => (
        <BookmarkCard
          key={entry.id}
          id={entry.id}
          node={entry.node}
          parentLabel={entry.parentLabel}
          Icon={entry.Icon}
          disabled={entry.disabled}
          manageMode={manageMode}
          padding={cardPadding}
          variant={variant}
          highlighted={entry.id === highlightId}
          focused={focusedIndex === i}
          setItemRef={(el) => setItemRef(i, el)}
          onFocus={() => setFocusedIndex(i)}
          onActivate={onActivate}
          onRemove={onRemove}
          onLongPress={onLongPress}
        />
      ))}
    </Box>
  );

  // Plain reuse (Home) with nothing to show: render nothing rather than the
  // "add one with +" hint, which only makes sense inside the Dashboard Menu.
  if (!showControls && entries.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Manage-mode toggle, right-aligned above the tiles: a cog to enter
          manage mode, swapped for "Ok" while managing. The row keeps a fixed
          height and is rendered in both states so toggling never shifts the
          grid below it. */}
      {showControls && entries.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            minHeight: 32,
          }}
        >
          {manageMode ? (
            <Button
              size="small"
              variant="outlined"
              onClick={onManageDone}
              disabled={saving}
              sx={dashboardButtonSx}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : "Ok"}
            </Button>
          ) : (
            <IconButton
              size="small"
              aria-label="Manage bookmarks"
              onClick={onManageStart}
              sx={{
                color: "#FFFFFF",
                opacity: 0.9,
                "&:hover": {
                  opacity: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                },
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Render only the grid when there are tiles; the "add one with the +"
          hint lives under the search field in the launchpad. */}
      {entries.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={validIds} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
});

export default BookmarksGrid;
